import { useRef, useState, type FormEvent } from 'react'
import { AlertTriangle, Check, ChevronRight, Loader2, ShieldAlert, Trash2, X } from 'lucide-react'
import * as api from '../api/auth'
import { ApiError } from '../api/auth'
import { ROLE_LABELS, type ManagedUser } from '../types/identity'
import { storeScopeLabel } from '../lib/labels'

const HANDLE = 48

/**
 * Permanent account deletion (D-41) with a deliberately heavy confirmation:
 *   1. slide the handle all the way across,
 *   2. press “Delete account permanently”,
 *   3. answer the final “Are you sure? (Final)”.
 * Only a system administrator sees this dialog, and the reason typed here is stored in the
 * audit trail next to the deletion.
 */
export function DeleteAccountDialog({
  target,
  onClose,
  onDeleted,
}: {
  target: ManagedUser
  onClose: () => void
  onDeleted: (message: string) => void
}) {
  const [reason, setReason] = useState('')
  const [progress, setProgress] = useState(0)
  const [slid, setSlid] = useState(false)
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const dragging = useRef(false)

  function ratio(clientX: number): number {
    const rect = trackRef.current?.getBoundingClientRect()
    if (!rect) return 0
    const usable = rect.width - HANDLE
    if (usable <= 0) return 0
    return Math.min(1, Math.max(0, (clientX - rect.left - HANDLE / 2) / usable))
  }

  function onPointerDown(event: React.PointerEvent<HTMLButtonElement>) {
    if (slid) return
    dragging.current = true
    event.currentTarget.setPointerCapture(event.pointerId)
    setProgress(ratio(event.clientX))
  }

  function onPointerMove(event: React.PointerEvent<HTMLButtonElement>) {
    if (!dragging.current || slid) return
    const next = ratio(event.clientX)
    if (next >= 0.96) {
      dragging.current = false
      setProgress(1)
      setSlid(true)
      return
    }
    setProgress(next)
  }

  function onPointerUp() {
    if (slid) return
    dragging.current = false
    setProgress(0)
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (slid) return
    if (event.key === 'ArrowRight') {
      const next = Math.min(1, progress + 0.25)
      setProgress(next)
      if (next >= 1) setSlid(true)
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setProgress(1)
      setSlid(true)
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    void (async () => {
      try {
        await api.deleteUser(target.id, reason.trim() || undefined)
        onDeleted(`${target.full_name} (${target.email}) was deleted permanently.`)
        onClose()
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : 'That deletion did not go through')
        setAsking(false)
        setSlid(false)
        setProgress(0)
      } finally {
        setBusy(false)
      }
    })()
  }

  const scope = storeScopeLabel(target)
  const store = scope === 'All' ? 'All stores' : `store ${scope}`

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-slate-900/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Delete ${target.full_name}`}
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onClose()
      }}
    >
      <form
        onSubmit={submit}
        className="max-h-[92dvh] w-full max-w-lg space-y-4 overflow-y-auto rounded-t-3xl border border-salsa-200 bg-white p-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-xl sm:max-h-[85vh] sm:rounded-2xl sm:p-5 dark:border-salsa-500/40 dark:bg-ink-900"
      >
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-salsa-500/15 text-salsa-600 dark:text-salsa-300">
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="leading-tight">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
              Delete this account permanently
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              No undo, no recycle bin. {target.full_name} · {target.email}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="icon-button ml-auto"
            title="Close"
            aria-label="Close"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <p className="text-xs text-slate-500 dark:text-slate-400">
          {ROLE_LABELS[target.role]} · {store}
        </p>

        <p className="flex items-start gap-2 rounded-xl border border-salsa-200 bg-salsa-50/70 px-3 py-2 text-xs text-salsa-800 dark:border-salsa-500/30 dark:bg-salsa-500/10 dark:text-salsa-200">
          <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            Sessions, tokens, authenticator and remembered devices go too. The{' '}
            <strong>audit trail survives</strong>, so this stays on record.
          </span>
        </p>

        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
          aria-label="Reason (stored in the audit log)"
          placeholder="Reason — e.g. left the company on 31 October (kept in the audit log)"
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5"
        />

        {!asking && (
          <div className="space-y-2">
            <div
              ref={trackRef}
              className="relative h-12 w-full select-none overflow-hidden rounded-xl border border-slate-200 bg-slate-100 dark:border-white/10 dark:bg-white/5"
            >
              <span className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                {slid ? 'Slide complete' : 'Slide to confirm'}
              </span>
              <button
                type="button"
                aria-label={slid ? 'Slide complete' : 'Slide to confirm'}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onKeyDown={onKeyDown}
                style={{ left: `calc(${progress * 100}% - ${progress * HANDLE}px)` }}
                className="absolute top-0 flex h-12 w-12 touch-none items-center justify-center rounded-xl bg-salsa-500 text-white shadow-md"
              >
                {slid ? <Check className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
              </button>
            </div>
            <button
              type="button"
              disabled={!slid || busy}
              onClick={() => setAsking(true)}
              className="w-full rounded-xl bg-salsa-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-salsa-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Delete account permanently
            </button>
          </div>
        )}

        {asking && (
          <div className="space-y-3 rounded-xl border border-salsa-300 bg-salsa-50/80 p-3 dark:border-salsa-500/40 dark:bg-salsa-500/10">
            <p className="text-xs font-semibold text-salsa-800 dark:text-salsa-200">
              Are you sure? (Final)
            </p>
            <p className="text-[11px] text-salsa-700 dark:text-salsa-300">
              {target.full_name}’s account is erased for good. This is the last step.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-xl bg-salsa-600 px-4 py-2 text-sm font-semibold text-white hover:bg-salsa-700 disabled:opacity-60"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                Yes, delete forever
              </button>
              <button
                type="button"
                onClick={() => setAsking(false)}
                disabled={busy}
                className="rounded-xl px-3 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-100 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-white/5"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-xl border border-salsa-200 bg-salsa-50/70 px-3 py-2 text-xs text-salsa-800 dark:border-salsa-500/25 dark:bg-salsa-500/10 dark:text-salsa-200">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}
      </form>
    </div>
  )
}
